/**
 * Lifecycle × Timing heatmap (6 × 6 matrix) — direct port of Kaiyang
 * `ReadinessAnalysisView` §Heatmap (chery-ai-contest-2026 path
 * src/components/readiness-board/ReadinessAnalysisView.tsx L128-177).
 *
 * Rows: LifecycleLayer A / B / C / D / P / U
 * Cols: pre / entry / post / recurring / event / phase-in
 *
 * Counts only `applicable` cards. Hidden / unknown / placeholder cards are
 * deliberately excluded — they don't represent an active engineering load.
 *
 * Color: Cobalt-blue alpha gradient (--ft-market-force at 0.08–1.0 alpha).
 * We use Cobalt instead of Kaiyang's literal `rgba(79,140,255,…)` so the
 * scale moves with theme switches (light/dark).
 */

import { useMemo } from "react";
import type { ComplianceRequirementCard, LifecycleLayer } from "@regpulse/contracts";

const ROWS: LifecycleLayer[] = ["A", "B", "C", "D", "P", "U"];

const COL_KEYS = [
  "pre",
  "entry",
  "post",
  "recurring",
  "event",
  "phase-in",
] as const;
type ColKey = (typeof COL_KEYS)[number];

const COL_LABEL: Record<ColKey, string> = {
  pre: "pre-market",
  entry: "at entry",
  post: "post-market",
  recurring: "recurring",
  event: "event-triggered",
  "phase-in": "phase-in",
};

function timingToCol(t: ComplianceRequirementCard["obligationTiming"]): ColKey {
  switch (t) {
    case "pre_market":
      return "pre";
    case "at_market_entry":
      return "entry";
    case "post_market":
      return "post";
    case "recurring":
      return "recurring";
    case "event_triggered":
      return "event";
    case "phase_in":
      return "phase-in";
  }
}

interface Props {
  cards: ComplianceRequirementCard[];
}

export function LifecycleTimingHeatmap({ cards }: Props) {
  const { matrix, max } = useMemo(() => {
    const m: Record<LifecycleLayer, Record<ColKey, number>> = {
      A: zeroRow(),
      B: zeroRow(),
      C: zeroRow(),
      D: zeroRow(),
      P: zeroRow(),
      U: zeroRow(),
    };
    for (const c of cards) {
      if (c.applicabilityCategory !== "applicable") continue;
      if (c.lifecycleState === "PLACEHOLDER") continue;
      m[c.layer][timingToCol(c.obligationTiming)] += 1;
    }
    let max = 0;
    for (const row of Object.values(m)) {
      for (const v of Object.values(row)) max = Math.max(max, v);
    }
    return { matrix: m, max };
  }, [cards]);

  return (
    <section
      data-testid="lifecycle-timing-heatmap"
      aria-labelledby="heatmap-title"
      style={{
        background: "var(--color-surface-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-8)",
        padding: "var(--space-12)",
      }}
    >
      <header style={{ marginBottom: "var(--space-8)" }}>
        <h3 id="heatmap-title" className="type-h3" style={{ margin: 0, fontSize: 13 }}>
          Lifecycle × Timing heatmap
        </h3>
        <p
          className="type-caption"
          style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)" }}
        >
          Visible applicable cards only. Excludes placeholder / curation-gap
          cards. Cell intensity ∝ count / max.
        </p>
      </header>
      <div style={{ overflowX: "auto" }}>
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}
          data-testid="heatmap-table"
        >
          <thead>
            <tr>
              <th style={{ ...thStyle, width: 48 }} scope="col" aria-label="lifecycle layer" />
              {COL_KEYS.map((c) => (
                <th key={c} style={thStyle} scope="col" data-col={c}>
                  {COL_LABEL[c]}
                </th>
              ))}
              <th style={{ ...thStyle, fontWeight: 700 }} scope="col">
                Σ
              </th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => {
              const rowSum = COL_KEYS.reduce((s, c) => s + matrix[row][c], 0);
              return (
                <tr key={row}>
                  <th
                    scope="row"
                    style={{
                      ...thStyle,
                      textAlign: "left",
                      fontWeight: 700,
                    }}
                    data-row={row}
                  >
                    {row}
                  </th>
                  {COL_KEYS.map((c) => {
                    const v = matrix[row][c];
                    const alpha = max === 0 ? 0.08 : v === 0 ? 0.08 : Math.max(0.2, v / max);
                    return (
                      <td
                        key={c}
                        data-cell={`${row}-${c}`}
                        data-count={v}
                        style={{
                          ...tdStyle,
                          background: `color-mix(in srgb, var(--ft-market-force) ${alpha * 100}%, transparent)`,
                          color: v === 0 ? "var(--color-text-disabled)" : "var(--color-text-primary)",
                          fontWeight: v > 0 ? 700 : 400,
                        }}
                      >
                        {v}
                      </td>
                    );
                  })}
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: 700,
                      borderLeft: "2px solid var(--color-border-strong)",
                    }}
                    data-row-sum={row}
                  >
                    {rowSum}
                  </td>
                </tr>
              );
            })}
            <tr>
              <th scope="row" style={{ ...thStyle, fontWeight: 700, textAlign: "left" }}>
                Σ
              </th>
              {COL_KEYS.map((c) => {
                const s = ROWS.reduce((acc, r) => acc + matrix[r][c], 0);
                return (
                  <td
                    key={c}
                    style={{
                      ...tdStyle,
                      fontWeight: 700,
                      borderTop: "2px solid var(--color-border-strong)",
                    }}
                    data-col-sum={c}
                  >
                    {s}
                  </td>
                );
              })}
              <td
                style={{
                  ...tdStyle,
                  fontWeight: 700,
                  borderTop: "2px solid var(--color-border-strong)",
                  borderLeft: "2px solid var(--color-border-strong)",
                }}
                data-grand-total
              >
                {ROWS.reduce((acc, r) => acc + COL_KEYS.reduce((s, c) => s + matrix[r][c], 0), 0)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p
        className="type-caption"
        data-testid="heatmap-mapper-note"
        style={{ margin: "var(--space-8) 0 0 0", color: "var(--color-text-secondary)", fontSize: 10 }}
      >
        v0.1 mapper note: <span className="type-mono">event-triggered</span> and{" "}
        <span className="type-mono">phase-in</span> timings are not yet emitted, and no rule is
        routed to the <strong>U</strong> lane (see ADR-0007, Phase 2–3). Empty cells in those
        columns / the U row may reflect this mapper gap, not an absence of obligations.
      </p>
    </section>
  );
}

function zeroRow(): Record<ColKey, number> {
  return {
    pre: 0,
    entry: 0,
    post: 0,
    recurring: 0,
    event: 0,
    "phase-in": 0,
  };
}

const thStyle: React.CSSProperties = {
  padding: "4px 6px",
  borderBottom: "1px solid var(--color-border)",
  color: "var(--color-text-secondary)",
  fontSize: 10,
  fontWeight: 600,
  textAlign: "center",
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "6px 4px",
  textAlign: "center",
  borderBottom: "1px solid var(--color-border)",
  fontVariantNumeric: "tabular-nums",
};
